import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Award, BookOpen } from 'lucide-react';

import ThemeToggle from '@/components/ThemeToggle';
import Badge from '@/components/quiz/Badge';
import { supabase } from '@/lib/supabase';
import {
  BADGE_TOPICS,
  BADGE_TIERS,
  computeMaxByTopic,
  getEarnedTier,
} from '@/data/badges';

export default function Badges() {
  const [maxByTopic, setMaxByTopic] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    // No user filter needed. RLS on training_scores restricts a normal user
    // to their own rows, so "select everything" already means "select mine".
    supabase
      .from('training_scores')
      .select('score, track, topic')
      .then(({ data, error }) => {
        if (!active) return;
        if (error) console.error('Loading badges failed:', error.message);
        setMaxByTopic(computeMaxByTopic(data || []));
        setLoading(false);
      });

    return () => {
      active = false;
    };
  }, []);

  const earnedCount = BADGE_TOPICS.reduce((n, topic) => {
    const max = maxByTopic[topic.key] ?? -1;
    return n + (getEarnedTier(max) ? 1 : 0);
  }, 0);

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-card/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-2xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link
            to="/"
            className="text-sm font-semibold tracking-tight text-foreground hover:text-primary transition-colors"
          >
            Customer Support Training Arena
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/kb-tests"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <BookOpen className="w-4 h-4" />
              <span className="hidden sm:inline">KB Tests</span>
            </Link>
            <span className="inline-flex items-center gap-1.5 text-sm text-primary">
              <Award className="w-4 h-4" />
              <span className="hidden sm:inline">Badges</span>
            </span>
            <Link
              to="/leaderboard"
              className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary transition-colors"
            >
              <Trophy className="w-4 h-4" />
              <span className="hidden sm:inline">Leaderboard</span>
            </Link>
            <ThemeToggle />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 sm:py-16">
        <div className="text-center mb-10">
          <span className="text-xs font-medium tracking-widest uppercase text-primary">
            Achievements
          </span>
          <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight text-foreground">
            Your badges
          </h1>
          <p className="mt-4 text-base leading-relaxed text-muted-foreground">
            Earn Bronze at 80%, Silver at 90%, and Gold at 100% in each topic.
          </p>
          {!loading && (
            <p className="mt-3 text-sm font-medium text-foreground">
              {earnedCount} of {BADGE_TOPICS.length} topics mastered
            </p>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-muted border-t-primary rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {BADGE_TOPICS.map((topic) => {
              const max = maxByTopic[topic.key] ?? -1;
              return (
                <div
                  key={topic.key}
                  className="rounded-2xl border border-border bg-card p-5"
                >
                  <div className="flex items-center justify-between mb-5">
                    <h3 className="text-sm font-semibold text-foreground">
                      {topic.label}
                    </h3>
                    <span className="text-xs font-medium text-muted-foreground">
                      Best: {max >= 0 ? `${Math.round(max)}%` : '—'}
                    </span>
                  </div>
                  <div className="flex items-start justify-around gap-2">
                    {BADGE_TIERS.map((tier) => (
                      <Badge
                        key={tier.key}
                        topic={topic}
                        tier={tier}
                        earned={max >= tier.threshold}
                        size="sm"
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
